// src/services/utils.service.ts
import axios from 'axios';
import api from './api.service';
import getApiBaseUrl from './getserver.service';

const apiURL = getApiBaseUrl();

// Interfaz para el objeto dentro del array 'data'
export interface DniTypeObject {
  id: number;
  isO2Country: string;
  kindOfDNI: string;
  mask?: string | null;
  prefix?: string | null;
  requiereRazonSocial: boolean;
}

// Interfaz para la respuesta completa del endpoint
interface DniTypeResponse {
  statusCode: number;
  message: string;
  data: DniTypeObject[];
}

// Interfaz para el objeto moneda
export interface CurrencyObject {
  id: number;
  isO2Code: string;
  isO3Currency: string;
}

// Interfaz para la respuesta de monedas
interface CurrencyResponse {
  statusCode: number;
  message: string;
  data: CurrencyObject[];
}

export interface LocationInfo {
  city: string;
  state: string;
  isO2Code: string;
  zipcode: string;
}

interface LocationByZipcodeResponse {
  statusCode: number;
  message: string;
  data: LocationInfo;
}

class UtilsService {
  async getDniTypes(countryCode: string): Promise<DniTypeObject[]> {
    if (!countryCode) {
      console.error("No countryCode provided to getDniTypes.");
      return []; // Retorna un array vacío si el país no está configurado
    }

    try {
      const response = await axios.get<DniTypeResponse>(`${apiURL}/get-country-dni`, {
        params: { country: countryCode },
      });
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error(
          "Error de Axios al llamar a get-country-dni:",
          error.response?.data || error.message
        );
      } else {
        console.error("Error inesperado al llamar a get-country-dni:", error);
      }
      throw error;
    }
  }

  async getCurrencies(): Promise<CurrencyObject[]> {
    try {
      const response = await api.get<CurrencyResponse>('/get-currencies');
      return response.data.data;
    } catch (error) {
      console.error("Error al obtener monedas:", error);
      return [];
    }
  }

  async getLocationByZipcode({
    iso2code,
    zipcode,
  }: {
    iso2code: string;
    zipcode: string;
  }): Promise<LocationInfo | null> {
    try {
      const response = await api.get<LocationByZipcodeResponse>(
        '/get-location-by-zipcode',
        {
          params: { iso2code, zipcode },
          headers: { 'X-Skip-Loading': true },
        }
      );
      if (response.data && response.data.data) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching location by zipcode:', error);
      // It's better to return null or a specific error object than to throw
      // especially if this is used for validation on-the-fly.
      return null;
    }
  }

  async addLocationGoogle(data: {
    ISO2Code: string;
    City: string;
    State: string;
    Zipcode: string;
    StateCode?: string;
  }): Promise<boolean> {
    try {
      const response = await api.post('/add-location-google', data, {
        headers: { 'X-Skip-Loading': true },
      });
      return response.data?.statusCode === 200 || response.status === 200;
    } catch (error) {
      console.error('Error adding location by google:', error);
      return false;
    }
  }
}

export const utilsService = new UtilsService();

export function applyDniPrefix(value: string, mask?: string | null): string {
  if (!mask || !value) return value;
  
  if (!mask.startsWith('^')) return value;

  // 1. Check if the mask starts with a character class like ^[VEve]
  if (mask.startsWith('^[')) {
    const closingBracketIndex = mask.indexOf(']');
    if (closingBracketIndex > 2) {
      const charClass = mask.slice(2, closingBracketIndex); // e.g. "VEve" or "A-Za-z0-9"
      
      // If it's a general character class range (like a-z, A-Z, 0-9, or contains range dashes), don't force a prefix
      const isGeneralClass = charClass.includes('a-z') || charClass.includes('A-Z') || charClass.includes('0-9');
      const hasRangeDash = charClass.includes('-') && !charClass.startsWith('-') && !charClass.endsWith('-');
      if (isGeneralClass || hasRangeDash) {
        return value;
      }

      let suffixAfterBracket = '';
      const remainder = mask.slice(closingBracketIndex + 1);
      const specialChars = ['.', '*', '+', '?', '(', ')', '[', ']', '{', '}', '|', '\\', '$'];
      for (let i = 0; i < remainder.length; i++) {
        const char = remainder[i];
        if (specialChars.includes(char)) {
          break;
        }
        suffixAfterBracket += char;
      }

      const firstChar = value.charAt(0);
      const isDigit = /^[0-9]$/.test(firstChar);
      const startsWithAllowedChar = Array.from(charClass).some(
        (c) => value.toLowerCase().startsWith(c.toLowerCase())
      );

      if (isDigit) {
        const defaultPrefix = charClass.charAt(0) + suffixAfterBracket;
        return defaultPrefix + value;
      } else if (!startsWithAllowedChar) {
        const defaultPrefix = charClass.charAt(0) + suffixAfterBracket;
        return defaultPrefix + value;
      }

      if (suffixAfterBracket) {
        for (let c of charClass) {
          if (value.toLowerCase().startsWith(c.toLowerCase())) {
            const afterChar = value.slice(c.length);
            if (afterChar && !afterChar.startsWith(suffixAfterBracket) && /^[0-9]/.test(afterChar)) {
              return value.slice(0, c.length) + suffixAfterBracket + afterChar;
            }
          }
        }
      }

      return value;
    }
  }

  // 2. Fallback to literal prefix extraction
  const remainder = mask.slice(1);
  let prefix = '';
  const specialChars = ['.', '*', '+', '?', '(', ')', '[', ']', '{', '}', '|', '\\', '$'];
  for (let i = 0; i < remainder.length; i++) {
    const char = remainder[i];
    if (specialChars.includes(char)) {
      break;
    }
    prefix += char;
  }
  
  if (!prefix) return value;
  
  if (prefix.toLowerCase().startsWith(value.toLowerCase())) {
    return value;
  }
  
  if (!value.toLowerCase().startsWith(prefix.toLowerCase())) {
    return prefix + value;
  }
  
  return value;
}

export function getMaxLengthFromMask(mask?: string | null): number | undefined {
  if (!mask) return undefined;
  
  let i = 0;
  let totalMax = 0;
  
  let cleanMask = mask;
  if (cleanMask.startsWith('^')) cleanMask = cleanMask.slice(1);
  if (cleanMask.endsWith('$')) cleanMask = cleanMask.slice(0, -1);
  
  while (i < cleanMask.length) {
    let char = cleanMask[i];
    let baseLength = 0;
    
    if (char === '[') {
      const close = cleanMask.indexOf(']', i);
      if (close !== -1) {
        baseLength = 1;
        i = close + 1;
      } else {
        baseLength = 1;
        i++;
      }
    } else if (char === '(') {
      const close = cleanMask.indexOf(')', i);
      if (close !== -1) {
        baseLength = 1;
        i = close + 1;
      } else {
        baseLength = 1;
        i++;
      }
    } else if (char === '\\') {
      baseLength = 1;
      i += 2;
    } else {
      baseLength = 1;
      i++;
    }
    
    if (i < cleanMask.length) {
      let nextChar = cleanMask[i];
      if (nextChar === '{') {
        const closeBrace = cleanMask.indexOf('}', i);
        if (closeBrace !== -1) {
          const content = cleanMask.slice(i + 1, closeBrace);
          if (content.includes(',')) {
            const parts = content.split(',');
            const maxVal = parts[1] ? parseInt(parts[1].trim(), 10) : parseInt(parts[0].trim(), 10);
            if (!isNaN(maxVal)) {
              baseLength = maxVal;
            }
          } else {
            const val = parseInt(content.trim(), 10);
            if (!isNaN(val)) {
              baseLength = val;
            }
          }
          i = closeBrace + 1;
        } else {
          i++;
        }
      } else if (nextChar === '?') {
        i++;
      } else if (nextChar === '*') {
        baseLength = 20;
        i++;
      } else if (nextChar === '+') {
        baseLength = 20;
        i++;
      }
    }
    
    totalMax += baseLength;
  }
  
  return totalMax > 0 ? totalMax : undefined;
}

export function getDniCompositeKey(t: DniTypeObject): string {
  return `${t.prefix || ''}_${t.kindOfDNI}`;
}

export function findDniTypeByCompositeKey(types: DniTypeObject[], key?: string | null): DniTypeObject | undefined {
  if (!key) return undefined;
  const found = types.find(t => getDniCompositeKey(t) === key);
  if (found) return found;
  return types.find(t => t.kindOfDNI === key);
}

export function getDniLabel(types: DniTypeObject[], key?: string | null): string {
  if (!key) return "";
  const found = findDniTypeByCompositeKey(types, key);
  if (found) {
    return found.prefix || found.kindOfDNI;
  }
  if (key.startsWith('_')) {
    return key.slice(1);
  }
  return key.replace('_', ' ');
}

export function formatTitleCase(text?: string | null): string {
  if (!text) return "";
  return text.replace(/\p{L}+/gu, (word) => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
}


